import { useEffect, useState } from "react";
import { toast } from "sonner";
import { rpc } from "../../lib/rpc";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { PhotoFilePicker, uploadPendingPhotos } from "../photo/PhotoFilePicker";

const EMPTY = {
    name: "",
    type: "bulk",
    category_id: "",
    ean: "",
    manufacturer_number: "",
    is_critical: false,
    min_quantity: "",
};

function fromArticle(a) {
    if (!a) return EMPTY;
    return {
        name: a.name || "",
        type: a.type || "bulk",
        category_id: a.category_id || "",
        ean: a.ean || "",
        manufacturer_number: a.manufacturer_number || "",
        is_critical: !!a.is_critical,
        min_quantity: a.min_quantity == null ? "" : String(a.min_quantity),
    };
}

/**
 * ArtikelFormDialog -- Anlegen (article=null) und Bearbeiten (#44, #114)
 * von Artikel-Stammdaten inkl. „Hochladen vom PC" für Fotos.
 *
 * onSaved(article) feuert nach erfolgreichem POST/PATCH und Foto-Upload.
 */
export function ArtikelFormDialog({ open, onClose, categories, article = null, onSaved, testId, cloned = false }) {
    const isEdit = !!article;
    const tid = testId || (isEdit ? "artikel-edit-dialog" : "artikel-create-dialog");
    // Test-IDs der Create-Felder bleiben stabil (bestehende Specs).
    const fid = isEdit ? "artikel-edit" : "artikel-new";
    const [form, setForm] = useState(fromArticle(article));
    const [pending, setPending] = useState([]);
    const [busy, setBusy] = useState(false);
    const [nameError, setNameError] = useState("");
    const [confirmDiscard, setConfirmDiscard] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(fromArticle(article));
            setPending([]);
            setNameError("");
            setConfirmDiscard(false);
        }
    }, [open, article]);

    // #136 Befund 5: Escape/Schließen verwirft ungespeicherte Eingaben nicht
    // mehr stillschweigend -- bei Änderungen kommt eine Rückfrage.
    const isDirty =
        JSON.stringify(form) !== JSON.stringify(fromArticle(article)) || pending.length > 0;
    const requestClose = () => {
        if (isDirty) setConfirmDiscard(true);
        else onClose();
    };

    const submit = async (event) => {
        event.preventDefault();
        if (!form.name.trim()) {
            setNameError("Pflichtfeld.");
            return;
        }
        setNameError("");
        setBusy(true);
        const payload = {
            name: form.name.trim(),
            type: form.type,
            category_id: form.category_id || null,
            ean: form.ean || null,
            manufacturer_number: form.manufacturer_number || null,
            is_critical: form.is_critical,
            min_quantity: form.min_quantity === "" ? null : Number(form.min_quantity),
        };
        const res = isEdit
            ? await rpc.patch(`/master/articles/${article.id}`, payload)
            : await rpc.post("/master/articles", payload);
        if (!res.ok) {
            setBusy(false);
            return toast.error(res.error);
        }
        const saved = res.data;
        let uploaded = 0;
        if (pending.length > 0) {
            uploaded = await uploadPendingPhotos(pending, {
                entityType: "article",
                entityId: saved.id,
            });
        }
        setBusy(false);
        toast.success(
            (isEdit ? "Artikel gespeichert." : "Artikel angelegt.") +
                (uploaded > 0 ? ` ${uploaded} Foto(s) hochgeladen.` : "")
        );
        setPending([]);
        onSaved?.(saved);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && requestClose()}>
            <DialogContent data-testid={tid}>
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Artikel bearbeiten" : "Neuer Artikel"}</DialogTitle>
                    <DialogDescription className="text-foreground" data-testid="artikel-form-description">
                        {cloned
                            ? "Die Kopie samt Artikelfotos ist bereits angelegt. Hersteller-Nr. und EAN bitte neu eintragen. Beim Schließen bleibt die Kopie bestehen."
                            : "Stammdaten bearbeiten und bei Bedarf weitere Fotos hochladen."}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="flex flex-col gap-3">
                    <Label htmlFor={`${fid}-name`}>Name *</Label>
                    <Input
                        id={`${fid}-name`}
                        autoFocus
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        aria-invalid={!!nameError}
                        data-testid={`${fid}-name`}
                    />
                    {nameError ? (
                        <p className="text-xs text-destructive" data-testid={`${fid}-name-error`}>{nameError}</p>
                    ) : null}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor={`${fid}-type`}>Typ</Label>
                            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                <SelectTrigger id={`${fid}-type`} data-testid={`${fid}-type`}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="bulk">bulk</SelectItem>
                                    <SelectItem value="single">single</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor={`${fid}-category`}>Kategorie</Label>
                            <Select
                                value={form.category_id}
                                onValueChange={(v) => setForm({ ...form, category_id: v })}
                            >
                                <SelectTrigger id={`${fid}-category`} data-testid={`${fid}-category`}>
                                    <SelectValue placeholder="—" />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>
                                            {c.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor={`${fid}-ean`}>EAN</Label>
                            <Input
                                id={`${fid}-ean`}
                                value={form.ean}
                                onChange={(e) => setForm({ ...form, ean: e.target.value })}
                                data-testid={`${fid}-ean`}
                            />
                        </div>
                        <div>
                            <Label htmlFor={`${fid}-min`}>Min-Bestand</Label>
                            <Input
                                id={`${fid}-min`}
                                type="number"
                                value={form.min_quantity}
                                onChange={(e) => setForm({ ...form, min_quantity: e.target.value })}
                                data-testid={`${fid}-min`}
                            />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor={`${fid}-manufacturer`}>Hersteller-Nr.</Label>
                        <Input
                            id={`${fid}-manufacturer`}
                            value={form.manufacturer_number}
                            onChange={(e) => setForm({ ...form, manufacturer_number: e.target.value })}
                            data-testid={`${fid}-manufacturer`}
                        />
                    </div>
                    <label className="flex items-center gap-2 text-xs">
                        <Checkbox
                            aria-label="kritisch"
                            checked={form.is_critical}
                            onCheckedChange={(v) => setForm({ ...form, is_critical: v })}
                            data-testid={`${fid}-critical`}
                        />
                        kritisch (im Leitstand und in der Datenqualität hervorgehoben)
                    </label>
                    <div>
                        <Label>Fotos</Label>
                        <div className="mt-1">
                            <PhotoFilePicker value={pending} onChange={setPending} testId={`${fid}-photos`} />
                        </div>
                    </div>
                    <Button type="submit" disabled={busy} data-testid={`${fid}-submit`}>
                        {busy ? "..." : isEdit ? "Speichern" : "Anlegen"}
                    </Button>
                </form>
                <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
                    <AlertDialogContent data-testid={`${fid}-discard-confirm`}>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Änderungen verwerfen?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Die Eingaben in diesem Dialog sind noch nicht gespeichert.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`${fid}-discard-cancel`}>
                                Weiter bearbeiten
                            </AlertDialogCancel>
                            <AlertDialogAction
                                data-testid={`${fid}-discard-submit`}
                                onClick={() => {
                                    setConfirmDiscard(false);
                                    onClose();
                                }}
                            >
                                Verwerfen
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </DialogContent>
        </Dialog>
    );
}
